import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import Field from '@components/Field';
import { DateTime } from 'luxon';

const buildValidationSchema = (definition, t) => {

    if (!definition || !definition.fields) {
        return Yup.object({});
    }

    const schemaFields = {};

    definition.fields.forEach(field => {

        // Ignorer les champs de type info
        if (field.type === 'info') {
            return;
        }

        // Validation de base pour les champs requis
        const requiredMsg = t('This field is required');
        let fieldSchema = field.required ?
            Yup.string().required(requiredMsg) :
            Yup.string();

        // Validation spécifique pour les champs de type date
        if (field.type === 'date') {
            fieldSchema = Yup.mixed()
                .transform((value) => {
                    if (!value) return undefined;
                    // Forcer la date à midi UTC pour éviter les problèmes de fuseau horaire
                    return value instanceof DateTime ?
                        value.toFormat('yyyy-MM-dd') : value;
                });
            if (field.required) {
                fieldSchema.required(requiredMsg);
            } else {
                fieldSchema.nullable();
            }
        }

        // Validation spécifique pour les champs de type datetime
        if (field.type === 'datetime') {
            fieldSchema = Yup.mixed()
                .transform((value) => {
                    if (!value) return undefined;
                    return value instanceof DateTime ?
                        value.toFormat('yyyy-MM-dd HH:mm:ss') : value;
                });
            if (field.required) {
                fieldSchema.required(requiredMsg);
            } else {
                fieldSchema.nullable();
            }
        }

        // Validation spécifique pour les champs de type checkbox/switch
        if (field.type === 'checkbox' || field.type === 'switch') {
            fieldSchema = Yup.boolean()
                .transform((value) => {
                    return Boolean(value);
                });
            if (field.required) {
                fieldSchema.required(requiredMsg);
            } else {
                fieldSchema.nullable().notRequired();
            }
        }

        // Validation spécifique pour les champs multiples
        if (field.type === 'selectmultiple' || field.type === 'checkboxes') {
            fieldSchema = Yup.array();
            if (field.required) {
                fieldSchema = fieldSchema
                    .min(1, requiredMsg)
                    .required(requiredMsg);
            } else {
                fieldSchema = fieldSchema.nullable();
            }
        }

        schemaFields[field.name] = fieldSchema;
    });

    return Yup.object(schemaFields);
};

const Form = ({ definition, onSubmit }) => {
    const { t } = useTranslation();

    const defaultValues = {};
    if (definition && definition.fields) {
        definition.fields.forEach(field => {
            if (field.type === 'selectmultiple' || field.type === 'checkboxes') {
                defaultValues[field.name] = [];
            } else {
                defaultValues[field.name] = '';
            }
        });
    }

    const validationSchema = buildValidationSchema(definition, t);
    const { control, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'all',
        reValidateMode: 'onBlur',
        defaultValues,
    });

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Box>
                <Box>
                    {definition && definition.fields && definition.fields.map((field, index) => (
                        <Box key={index} mb={3}>
                            <Controller
                                name={field.name}
                                control={control}
                                render={({ field: fieldToRender }) => (
                                    <Field field={fieldToRender} definition={field} error={errors[field.name]?.message} />
                                )}
                            />
                        </Box>
                    ))}
                </Box>
                <Box sx={{ mt: 4 }}>
                    <Button type="submit" variant="contained" color="primary" size="large">
                        {t('Save')}
                    </Button>
                </Box>
            </Box>
        </form>
    );
};

export default Form;
